import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { hasRole as hasRoleFn, type SessionUser } from "@/lib/auth";

/**
 * 현재 사용자 전역 상태 (클라이언트 전용)
 *
 * 설계 원칙:
 * - Keycloak 인증(또는 dev 모킹) 후 세션 사용자 정보를 저장해 UI 초기화
 * - 인증 판단 RBAC은 서버에서 재수행; 여기는 UI 렌더링 보조용(메뉴 노출 등)
 * - 민감 정보(access_token) 저장 금지 — 토큰은 lib/auth 모듈 스코프가 단일 보관
 *
 * 사용자 타입 단일 출처:
 * - 세션 사용자 형태는 lib/auth 의 SessionUser 를 그대로 사용 (중복 정의 금지).
 */

/** @deprecated lib/auth 의 SessionUser 를 사용. 하위 호환 별칭. */
export type CurrentUser = SessionUser;

interface UserStore {
  /** 현재 세션 사용자 (미인증 시 null) */
  user: SessionUser | null;
  /**
   * 세션 복원 완료 여부.
   * OIDC 세션 복원(getUser) 또는 dev 모킹 주입이 끝나면 true.
   * AuthGuard 가 복원 중 깜빡임을 막기 위해 사용.
   */
  isHydrated: boolean;
  /** 세션 사용자 설정 (로그인/콜백/useMe 동기화 시 호출) */
  setUser: (user: SessionUser | null) => void;
  /** hydration 완료 표시 */
  setHydrated: () => void;
  /** 현재 사용자가 요구 역할 중 하나라도 보유하는지 (UX 보조, 서버 재검증) */
  hasRole: (required: string | string[]) => boolean;
  /** 세션 초기화 (로그아웃) */
  clearUser: () => void;
}

export const useUserStore = create<UserStore>()(
  devtools(
    (set, get) => ({
      user: null,
      isHydrated: false,
      setUser: (user) => set({ user }, false, "setUser"),
      setHydrated: () => set({ isHydrated: true }, false, "setHydrated"),
      hasRole: (required) => {
        const { user } = get();
        if (!user) return false;
        return hasRoleFn(user, required);
      },
      clearUser: () => set({ user: null }, false, "clearUser"),
    }),
    { name: "eqms/user" },
  ),
);
